import React, { useState, useMemo } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Button,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import SpeedIcon from '@mui/icons-material/Speed';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AddIcon from '@mui/icons-material/Add';

export default function TaxiInformation() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();

  // Placeholder data - will be replaced with real data
  const [data] = useState([
    {
      id: 1,
      registrationNumber: 'EL 12345',
      make: 'Tesla',
      model: 'Model S',
      year: 2023,
      ownerName: 'Oslo Taxi AS',
      meterNumber: 'TX-001234',
      status: 'active',
      lastInspection: '2024-01-15',
    },
    {
      id: 2,
      registrationNumber: 'EK 67890',
      make: 'Mercedes',
      model: 'EQE',
      year: 2022,
      ownerName: 'Bergen Drosje AS',
      meterNumber: 'TX-005678',
      status: 'active',
      lastInspection: '2024-02-20',
    },
    {
      id: 3,
      registrationNumber: 'EB 11223',
      make: 'Volvo',
      model: 'XC90',
      year: 2021,
      ownerName: 'Oslo Taxi AS',
      meterNumber: 'TX-003344',
      status: 'maintenance',
      lastInspection: '2023-12-01',
    },
    {
      id: 4,
      registrationNumber: 'EV 44556',
      make: 'Audi',
      model: 'e-tron',
      year: 2023,
      ownerName: 'Trondheim Taxi',
      meterNumber: 'TX-007788',
      status: 'inactive',
      lastInspection: '2024-01-30',
    },
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRow, setSelectedRow] = useState(null);

  const filteredData = useMemo(() => {
    let result = data;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.registrationNumber && d.registrationNumber.toLowerCase().includes(query)) ||
        (d.make && d.make.toLowerCase().includes(query)) ||
        (d.model && d.model.toLowerCase().includes(query)) ||
        (d.ownerName && d.ownerName.toLowerCase().includes(query)) ||
        (d.meterNumber && d.meterNumber.toLowerCase().includes(query))
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(d => d.status === statusFilter);
    }

    return result;
  }, [data, searchQuery, statusFilter]);

  // Summary stats
  const summaryData = useMemo(() => ({
    totalVehicles: data.length,
    activeVehicles: data.filter(d => d.status === 'active').length,
    maintenanceVehicles: data.filter(d => d.status === 'maintenance').length,
    inactiveVehicles: data.filter(d => d.status === 'inactive').length,
  }), [data]);

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: t('active') || 'Active', bgcolor: '#DCFCE7', color: '#16A34A' },
      maintenance: { label: t('maintenance') || 'Maintenance', bgcolor: '#FEF3C7', color: '#D97706' },
      inactive: { label: t('inactive') || 'Inactive', bgcolor: '#FEE2E2', color: '#DC2626' },
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bgcolor,
          color: config.color,
          fontWeight: 600,
          fontSize: 12,
        }}
      />
    );
  };

  const columns = [
    {
      title: t('vehicle') || 'Vehicle',
      field: 'registrationNumber',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DirectionsCarIcon sx={{ color: '#2563EB', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
              {rowData.registrationNumber || '-'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
              {rowData.make} {rowData.model} ({rowData.year})
            </Typography>
          </Box>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('owner') || 'Owner',
      field: 'ownerName',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {rowData.ownerName || '-'}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('meter_number') || 'Meter Number',
      field: 'meterNumber',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SpeedIcon sx={{ fontSize: 16, color: '#6B7280' }} />
          <Typography sx={{ fontSize: 14, color: '#374151', fontFamily: 'monospace' }}>
            {rowData.meterNumber || '-'}
          </Typography>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('last_inspection') || 'Last Inspection',
      field: 'lastInspection',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 14, color: '#6B7280' }} />
          <Typography sx={{ fontSize: 13, color: '#374151' }}>
            {rowData.lastInspection || '-'}
          </Typography>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('status') || 'Status',
      field: 'status',
      render: rowData => getStatusChip(rowData.status),
      cellStyle: { padding: '14px 16px' },
    },
  ];

  return (
    <Box style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
            {t('taxi_information') || 'Taxi Information'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
            {t('taxi_information_description') || 'Manage vehicle details and taximeter information'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            bgcolor: '#111827',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
            '&:hover': { bgcolor: '#374151' },
          }}
        >
          {t('add_vehicle') || 'Add Vehicle'}
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LocalTaxiIcon sx={{ color: '#2563EB', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('total_vehicles') || 'Total'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {summaryData.totalVehicles}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DirectionsCarIcon sx={{ color: '#16A34A', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('active') || 'Active'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#16A34A' }}>
              {summaryData.activeVehicles}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DirectionsCarIcon sx={{ color: '#D97706', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('maintenance') || 'Maintenance'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#D97706' }}>
              {summaryData.maintenanceVehicles}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DirectionsCarIcon sx={{ color: '#DC2626', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('inactive') || 'Inactive'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#DC2626' }}>
              {summaryData.inactiveVehicles}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Filter Toolbar */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder={t('search') || 'Search...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 280,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#F9FAFB',
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, bgcolor: '#F9FAFB' }}
          >
            <MenuItem value="all">{t('all_status') || 'All Status'}</MenuItem>
            <MenuItem value="active">{t('active') || 'Active'}</MenuItem>
            <MenuItem value="maintenance">{t('maintenance') || 'Maintenance'}</MenuItem>
            <MenuItem value="inactive">{t('inactive') || 'Inactive'}</MenuItem>
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: 13, color: '#6B7280', ml: 'auto' }}>
          <strong style={{ color: '#111827' }}>{filteredData.length}</strong> {t('results') || 'results'}
        </Typography>
      </Box>

      {/* Table */}
      <MaterialTable
        icons={TableIcons}
        title={null}
        columns={columns}
        data={filteredData}
        onRowClick={(evt, selectedRow) => setSelectedRow(selectedRow.tableData.id)}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: 12,
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
        }}
        options={{
          ...tableOptions,
          search: false,
          toolbar: false,
          showTitle: false,
          rowStyle: rowData => ({
            backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
          }),
        }}
        localization={{
          body: { emptyDataSourceMessage: t('no_data') || 'No data to display' },
          pagination: {
            labelDisplayedRows: '{from}-{to} ' + (t('of') || 'of') + ' {count}',
            firstTooltip: t('first_page_tooltip'),
            previousTooltip: t('previous_page_tooltip'),
            nextTooltip: t('next_page_tooltip'),
            lastTooltip: t('last_page_tooltip'),
          },
        }}
      />
    </Box>
  );
}
